import type { Bulletin } from '@shared/types/Bulletin';
import { TRIMESTRE_RE, type DtsLigne } from '@shared/types/Dts';
import type { Salarie } from '@shared/types/Salarie';
import type { Tenant } from '@shared/types/Tenant';
import { PLAFOND_CNSS } from '@shared/constants/Taux';
import { retenirBulletinsUniques } from '@domain/paie/BulletinsUniques';
import { montantCotisation, roundFCFA } from '@shared/utils/round';
import {
  TAUX_VENTILATION_CNSS_DEFAUT,
  ventilationCnssDuBulletin,
  type VentilationCnssPatronale,
} from '@domain/paie/VentilationCnss';

export { TRIMESTRE_RE };

/**
 * Ventilation de la CNSS patronale d'une ligne de DTS / AD-01 : prestations familiales 5 %, AT-MP 2 %, VID 11 %
 * (total = 18 %). Une ligne enregistrée avant la ventilation est reconstituée depuis sa base plafonnée et son total patronal :
 * le total n'est jamais modifié, seule la répartition est calculée.
 */
export function ventilationLigneDts(l: DtsLigne): VentilationCnssPatronale {
  if (l.patronale_pf !== undefined && l.patronale_atmp !== undefined && l.patronale_vid !== undefined) {
    return {
      pf: l.patronale_pf,
      atmp: l.patronale_atmp,
      vid: l.patronale_vid,
      total: l.patronale_pf + l.patronale_atmp + l.patronale_vid,
    };
  }
  const pf = montantCotisation(l.base_cnss, TAUX_VENTILATION_CNSS_DEFAUT.pf);
  const atmp = montantCotisation(l.base_cnss, TAUX_VENTILATION_CNSS_DEFAUT.atmp);
  return { pf, atmp, vid: Math.max(0, l.patronale - pf - atmp), total: l.patronale };
}

/** Totaux de la ventilation sur un ensemble de lignes. */
export function totauxVentilationDts(lignes: readonly DtsLigne[]): VentilationCnssPatronale {
  return lignes.reduce<VentilationCnssPatronale>(
    (t, l) => {
      const v = ventilationLigneDts(l);
      return { pf: t.pf + v.pf, atmp: t.atmp + v.atmp, vid: t.vid + v.vid, total: t.total + v.total };
    },
    { pf: 0, atmp: 0, vid: 0, total: 0 },
  );
}

/** Plafond mensuel CNSS Gabon (brut soumis) — alias référentiel `Taux.ts`. */
export const PLAFOND_CNSS_MENSUEL = PLAFOND_CNSS;

const MOIS_LIBELLES = [
  'janvier–mars',
  'avril–juin',
  'juillet–septembre',
  'octobre–décembre',
] as const;

export interface DtsEcheance {
  label: string;
  date: string;
  jours: number;
  alerte: boolean;
}

export interface DtsLigneMensuelle {
  numero_cnss: string;
  nom: string;
  prenom: string;
  periode: string;
  brut_plafonne: number;
  cot_sal: number;
  cot_pat: number;
  /** Ventilation de `cot_pat` : prestations familiales 5 %, AT-MP 2 %, VID 11 %. */
  cot_pat_pf: number;
  cot_pat_atmp: number;
  cot_pat_vid: number;
}

export interface TrimestreDts {
  annee: number;
  trimestre: 1 | 2 | 3 | 4;
}

export interface DtsAgrege {
  trimestre: string;
  /** Les trois mois civils du trimestre. */
  periodes: string[];
  /** Mois du trimestre qui portent au moins un bulletin retenu (ordre du calendrier). */
  periodes_presentes: string[];
  lignes: DtsLigne[];
  total_brut: number;
  total_base_cnss: number;
  total_patronale: number;
  total_salariale: number;
  effectif: number;
}

/** Parse `2026-T1` … `2026-T4`. */
export function parseTrimestre(code: string): TrimestreDts {
  const m = TRIMESTRE_RE.exec(code);
  if (!m) throw new Error('Trimestre invalide (attendu AAAA-T1 à AAAA-T4)');
  return { annee: Number(m[1]), trimestre: Number(m[2]) as 1 | 2 | 3 | 4 };
}

export function formatTrimestre(annee: number, trimestre: 1 | 2 | 3 | 4): string {
  return `${annee}-T${trimestre}`;
}

/** Mois civils du trimestre : T1 → jan–mar. */
export function periodesDuTrimestre(code: string): [string, string, string] {
  const { annee, trimestre } = parseTrimestre(code);
  const debut = (trimestre - 1) * 3 + 1;
  return [0, 1, 2].map((i) => `${annee}-${String(debut + i).padStart(2, '0')}`) as [
    string,
    string,
    string,
  ];
}

/** Trimestre CNSS correspondant à une période `YYYY-MM`. */
export function trimestreDePeriode(periode: string): string {
  const [anneeBrute, moisBrut] = periode.split('-');
  const annee = Number(anneeBrute);
  const mois = Number(moisBrut);
  if (!Number.isFinite(annee) || mois < 1 || mois > 12) {
    throw new Error('Période invalide (attendu YYYY-MM)');
  }
  const trimestre = Math.ceil(mois / 3) as 1 | 2 | 3 | 4;
  return formatTrimestre(annee, trimestre);
}

export function libelleTrimestre(code: string): string {
  const { annee, trimestre } = parseTrimestre(code);
  return `${annee} T${trimestre} (${MOIS_LIBELLES[trimestre - 1]})`;
}

export function optionsTrimestres(
  periodesConnues: readonly string[] = [],
  anneeRef = new Date().getFullYear(),
): { value: string; label: string }[] {
  const annees = new Set<number>([anneeRef - 1, anneeRef]);
  for (const p of periodesConnues) {
    const y = Number(p.slice(0, 4));
    if (Number.isFinite(y) && y >= 2000 && y <= 2100) annees.add(y);
  }
  const codes: string[] = [];
  for (const y of [...annees].sort((a, b) => a - b)) {
    for (const t of [1, 2, 3, 4] as const) codes.push(formatTrimestre(y, t));
  }
  return codes.map((value) => ({ value, label: libelleTrimestre(value) }));
}

function numeroCnss(salarie: Salarie | undefined): string {
  if (!salarie) return '';
  return salarie.numero_cnss || salarie.cnss || '';
}

function bulletinEligibleDts(b: Bulletin): boolean {
  return b.statut === 'valide' || b.statut === 'paye';
}

function nomSalarie(b: Bulletin, salarie: Salarie | undefined): string {
  const depuisFiche = salarie ? `${salarie.prenom} ${salarie.nom}` : '';
  return b.nom_complet || depuisFiche || b.matricule || salarie?.matricule || '';
}

function echapperCsv(value: string): string {
  return /[;"\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Agrégation nominative des bulletins d'un trimestre pour la DTS CNSS.
 * Refuse un trimestre sans bulletin (hors annulés).
 */
export class DtsService {
  static agreger(
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    trimestre: string,
  ): DtsAgrege {
    const periodes = periodesDuTrimestre(trimestre);
    const dansTrimestre = new Set(periodes);
    // Un seul bulletin par (salarié, période) : des doublons historiques ne gonflent ni l'effectif ni les bases.
    const retenus = retenirBulletinsUniques(
      bulletins.filter((b) => dansTrimestre.has(b.periode) && bulletinEligibleDts(b)),
    );
    if (retenus.length === 0) {
      throw new Error(`Aucun bulletin sur ${libelleTrimestre(trimestre)}`);
    }

    const parId = new Map(salaries.map((s) => [s.id, s]));
    const lignes = new Map<string, DtsLigne & { mois: Set<string> }>();

    for (const b of retenus) {
      let line = lignes.get(b.salarie_id);
      if (!line) {
        const sal = parId.get(b.salarie_id);
        line = {
          salarie_id: b.salarie_id,
          matricule: b.matricule || sal?.matricule || '',
          nom_complet: nomSalarie(b, sal),
          numero_cnss: numeroCnss(sal),
          brut: 0,
          base_cnss: 0,
          patronale: 0,
          salariale: 0,
          mois_presents: 0,
          patronale_pf: 0,
          patronale_atmp: 0,
          patronale_vid: 0,
          mois: new Set<string>(),
        };
        lignes.set(b.salarie_id, line);
      }
      line.brut += b.brut_imposable || 0;
      // Plafond CNSS par salarié et par mois (décision du 16/10/2026), jamais sur un cumul.
      line.base_cnss += Math.min(b.base_cnss || 0, PLAFOND_CNSS_MENSUEL);
      line.patronale += (b.cnss_pf_emp || 0) + (b.cnss_rp_emp || 0) + (b.cnss_vid_emp || 0);
      const ventilation = ventilationCnssDuBulletin(b);
      line.patronale_pf = (line.patronale_pf ?? 0) + ventilation.pf;
      line.patronale_atmp = (line.patronale_atmp ?? 0) + ventilation.atmp;
      line.patronale_vid = (line.patronale_vid ?? 0) + ventilation.vid;
      line.salariale += b.cnss_vid_sal || 0;
      line.mois.add(b.periode);
    }

    const arr: DtsLigne[] = Array.from(lignes.values())
      .map(({ mois, ...l }) => ({
        ...l,
        mois_presents: mois.size,
        brut: roundFCFA(l.brut),
        base_cnss: roundFCFA(l.base_cnss),
        patronale: roundFCFA(l.patronale),
        salariale: roundFCFA(l.salariale),
        patronale_pf: roundFCFA(l.patronale_pf ?? 0),
        patronale_atmp: roundFCFA(l.patronale_atmp ?? 0),
        patronale_vid: roundFCFA(l.patronale_vid ?? 0),
      }))
      .sort((a, b) => a.matricule.localeCompare(b.matricule, 'fr'));

    if (arr.length === 0) {
      throw new Error(
        `Trimestre ${trimestre} : aucun salarié rattaché aux bulletins — DTS impossible`,
      );
    }

    return {
      trimestre,
      periodes: [...periodes],
      periodes_presentes: periodes.filter((p) => retenus.some((b) => b.periode === p)),
      lignes: arr,
      total_brut: arr.reduce((s, l) => s + l.brut, 0),
      total_base_cnss: arr.reduce((s, l) => s + l.base_cnss, 0),
      total_patronale: arr.reduce((s, l) => s + l.patronale, 0),
      total_salariale: arr.reduce((s, l) => s + l.salariale, 0),
      effectif: arr.length,
    };
  }

  /** Bordereau nominatif CSV (séparateur `;`, cartouche employeur). */
  static exporterCsv(
    agrege: Pick<
      DtsAgrege,
      | 'trimestre'
      | 'lignes'
      | 'effectif'
      | 'total_brut'
      | 'total_base_cnss'
      | 'total_patronale'
      | 'total_salariale'
    >,
    entreprise?: Tenant | null,
  ): string {
    const sep = ';';
    const colonnes = [
      'Matricule',
      'Salarié',
      'N° CNSS',
      'Brut',
      'Base plaf.',
      'Prestations familiales (emp.)',
      'AT-MP (emp.)',
      'VID (emp.)',
      'Patronale',
      'Salariale',
      'Total CNSS',
      'Mois',
    ];
    const paires: Array<[string, string]> = [
      ['Déclaration', 'DTS'],
      ['Période', libelleTrimestre(agrege.trimestre)],
      ['Employeur', entreprise?.raison_sociale || entreprise?.nom || ''],
      ['NIF', entreprise?.nif || ''],
      ['RCCM', entreprise?.rccm || ''],
      ['N° CNSS employeur', entreprise?.parametres?.numero_cnss_employeur || ''],
      ['N° CNAMGS employeur', entreprise?.parametres?.numero_cnamgs_employeur || ''],
      ['Effectif déclaré', String(agrege.effectif)],
    ];
    const lignes = [
      ...paires.map(([cle, valeur]) => [cle, echapperCsv(valeur)].join(sep)),
      '',
      colonnes.join(sep),
    ];
    for (const l of agrege.lignes) {
      lignes.push(
        [
          echapperCsv(l.matricule),
          echapperCsv(l.nom_complet),
          echapperCsv(l.numero_cnss),
          l.brut,
          l.base_cnss,
          ventilationLigneDts(l).pf,
          ventilationLigneDts(l).atmp,
          ventilationLigneDts(l).vid,
          l.patronale,
          l.salariale,
          l.patronale + l.salariale,
          l.mois_presents,
        ].join(sep),
      );
    }
    if (agrege.lignes.length > 0) {
      const t = totauxVentilationDts(agrege.lignes);
      lignes.push(
        [
          'TOTAL',
          '',
          '',
          agrege.total_brut,
          agrege.total_base_cnss,
          t.pf,
          t.atmp,
          t.vid,
          agrege.total_patronale,
          agrege.total_salariale,
          agrege.total_patronale + agrege.total_salariale,
          '',
        ].join(sep),
      );
    }
    return lignes.join('\n');
  }

  static nomFichier(trimestre: string): string {
    return `declaration_dts_${trimestre}.csv`;
  }

  /**
   * Export CSV mensuel : 1 ligne par salarié × mois.
   * Brut plafonné = MIN(brut, 1 500 000) ; cot. sal. 5 % ; cot. pat. 18 %.
   */
  static exporterMensuel(
    bulletins: readonly Bulletin[],
    salaries: readonly Salarie[],
    entreprise?: Tenant | null,
  ): string {
    const sep = ';';
    const parId = new Map(salaries.map((s) => [s.id, s]));
    const retenus = bulletins
      .filter(bulletinEligibleDts)
      .slice()
      .sort(
        (a, b) =>
          a.periode.localeCompare(b.periode) ||
          (a.matricule ?? '').localeCompare(b.matricule ?? '', 'fr'),
      );

    const lignes: DtsLigneMensuelle[] = retenus.map((b) => {
      const sal = parId.get(b.salarie_id);
      const brutPlafonne = roundFCFA(b.base_cnss || 0);
      const cotPat = roundFCFA((b.cnss_pf_emp || 0) + (b.cnss_rp_emp || 0) + (b.cnss_vid_emp || 0));
      const ventilation = ventilationCnssDuBulletin(b);
      return {
        numero_cnss: numeroCnss(sal),
        nom: sal?.nom || '',
        prenom: sal?.prenom || '',
        periode: b.periode,
        brut_plafonne: brutPlafonne,
        cot_sal: roundFCFA(b.cnss_vid_sal || 0),
        cot_pat: cotPat,
        cot_pat_pf: ventilation.pf,
        cot_pat_atmp: ventilation.atmp,
        cot_pat_vid: ventilation.vid,
      };
    });

    const cartouche: string[] = [
      ['Déclaration', 'DTS mensuelle'].join(sep),
      ['Employeur', echapperCsv(entreprise?.raison_sociale || entreprise?.nom || '')].join(sep),
      ['NIF', echapperCsv(entreprise?.nif || '')].join(sep),
      ['RCCM', echapperCsv(entreprise?.rccm || '')].join(sep),
      ['N° CNSS employeur', echapperCsv(entreprise?.parametres?.numero_cnss_employeur || '')].join(
        sep,
      ),
      [
        'N° CNAMGS employeur',
        echapperCsv(entreprise?.parametres?.numero_cnamgs_employeur || ''),
      ].join(sep),
      ['Effectif déclaré', String(lignes.length)].join(sep),
      '',
    ];
    const header = [
      'N° CNSS',
      'Nom',
      'Prénom',
      'Période',
      'Brut plafonné',
      'Cot sal 5%',
      'Cot pat 18%',
      'dont prestations familiales 5%',
      'dont AT-MP 2%',
      'dont VID 11%',
    ].join(sep);

    const rows = lignes.map((l) =>
      [
        echapperCsv(l.numero_cnss),
        echapperCsv(l.nom),
        echapperCsv(l.prenom),
        echapperCsv(l.periode),
        l.brut_plafonne,
        l.cot_sal,
        l.cot_pat,
        l.cot_pat_pf,
        l.cot_pat_atmp,
        l.cot_pat_vid,
      ].join(sep),
    );

    const totalBrut = lignes.reduce((s, l) => s + l.brut_plafonne, 0);
    const totalSal = lignes.reduce((s, l) => s + l.cot_sal, 0);
    const totalPat = lignes.reduce((s, l) => s + l.cot_pat, 0);
    const pied = [
      'TOTAL',
      '',
      '',
      '',
      totalBrut,
      totalSal,
      totalPat,
      lignes.reduce((s, l) => s + l.cot_pat_pf, 0),
      lignes.reduce((s, l) => s + l.cot_pat_atmp, 0),
      lignes.reduce((s, l) => s + l.cot_pat_vid, 0),
    ].join(sep);

    return [...cartouche, header, ...rows, pied].join('\n');
  }

  /**
   * Échéances DTS trimestrielles : 30 avr / 31 juil / 31 oct / 31 jan.
   * `alerte` si l’échéance est dans les 15 jours (incluant le jour J).
   */
  static echeancesDtsProchaines(now = new Date()): DtsEcheance[] {
    const y = now.getFullYear();
    const candidates: Array<{ label: string; date: Date }> = [
      { label: 'DTS T1 (janvier–mars)', date: new Date(y, 3, 30) },
      { label: 'DTS T2 (avril–juin)', date: new Date(y, 6, 31) },
      { label: 'DTS T3 (juillet–septembre)', date: new Date(y, 9, 31) },
      { label: 'DTS T4 (octobre–décembre)', date: new Date(y + 1, 0, 31) },
      // Si on est après le 30/04, la T1 de l’année suivante reste utile en fin d’année
      { label: 'DTS T1 (janvier–mars)', date: new Date(y + 1, 3, 30) },
    ];

    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return candidates
      .map(({ label, date }) => {
        const jours = Math.round((date.getTime() - startOfDay.getTime()) / 86_400_000);
        const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        return {
          label,
          date: iso,
          jours,
          alerte: jours >= 0 && jours <= 15,
        };
      })
      .filter((e) => e.jours >= 0)
      .sort((a, b) => a.jours - b.jours)
      .slice(0, 4);
  }
}
